/**
 * A module for handling our site's interaction with the userflow.js plugin
 * https://userflow.com/docs
 * This module is only active in Stage and Prod environments
 * See advance events in: user-flow-browser-handling.js for OS & browser-specific handling
 */

import userflow from 'userflow.js';

import { isFeatureFlagEnabled } from '../api';
import { isStageEnvironment, isProductionEnvironment, parseUserRole } from '../helpers';

const initLoginListener = (appSlug) => {
    window.addEventListener(window.$wx.mfeLoginCompleteEvent.type, () =>
        initUserFlow(appSlug)
    );
};

const initLogoutListener = () => {
    window.addEventListener(
        window.$wx.mfeLogoutCompleteEvent.type,
        deactivateUserFlow
    );
};

/**
 * Initialize the userflow.js plugin
 */
export const initUserFlow = async (appSlug) => {
    // if not stage or prod, don't initialize
    if (!isProductionEnvironment() && !isStageEnvironment()) {
        return;
    }

    try {
        const isUserFlowEnabled = await isFeatureFlagEnabled(
            'userFlow',
            appSlug
        );
        if (isUserFlowEnabled) {
            activateUserFlow(appSlug);
        }
    } catch (error) {
        console.error('Failed to initialize userFlow', error);
    }
};

function deactivateUserFlow() {
    userflow.endAll();
}

/**
 * Activate the userflow.js plugin.
 * Calling this anywhere will enable it across the site.
 * Will only be called if Stage or Prod environment.
 */
function activateUserFlow(appSlug) {
    const user = JSON.parse(localStorage.getItem('wxPortal.User'));

    if (!user) {
        initLoginListener(appSlug);
        return;
    }

    initLogoutListener();

    isStageEnvironment()
        ? userflow.init('ct_kzssefhmsfehvnwmctkaqgk44e') // stage key
        : userflow.init('ct_lpc7upt5rrcqlec2bd44pbjnma'); // prod key

    userflow.identify(user.id, {
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        signed_up_at: user.created_at,
        updated_at: user.updated_at,
        roles: user.roles.join(', '),
        role: parseUserRole(user, appSlug) || 'staff',
    });

    // Initialize the option to minimize the default userflow icon
    monitorAndMinimizeUserflow();
}


// Give users the opportunity to "hide" (minimize) the default Userflow resource center icon

// Only display the minimize button if the Userflow resource center icon is present on screen
function monitorAndMinimizeUserflow() {
    const minimizeUserflowButton = document.getElementById('minimizeUserflowButton');
    const dockedUserflowButton = document.getElementById('dockedUserflowButton');

    function updateVisibility() {
        const userflowIcon = document.querySelector('#userflow-ui .userflowjs-resource-center');
        minimizeUserflowButton.style.display = userflowIcon ? 'flex' : 'none';

        if (!userflowIcon) {
            dockedUserflowButton.style.display = 'none';
        }
    }

    // Selecting the minimize button will hide the normal userflow resource center icon
    // and show a docked "replacement" button
    minimizeUserflowButton.addEventListener('click', () => {
        const userflowIcon = document.querySelector('#userflow-ui .userflowjs-resource-center');
        if (userflowIcon) {
            userflowIcon.style.display = 'none';
        }
        minimizeUserflowButton.style.display = 'none';
        dockedUserflowButton.style.display = 'flex';
    });

    // Selecting the docked "replacement" button will show the normal userflow resource center icon once again
    dockedUserflowButton.addEventListener('click', () => {
        const userflowIconElement = document.querySelector('#userflow-ui .userflowjs-resource-center');
        if (userflowIconElement) {
            userflowIconElement.style.display = 'flex';
        }
        minimizeUserflowButton.style.display = 'flex';
        dockedUserflowButton.style.display = 'none';
    });

    // Initial visibility check
    updateVisibility();

    // Observe DOM changes to update visibility
    const observer = new MutationObserver(updateVisibility);
    observer.observe(document.body, { childList: true, subtree: true });
}

// Monitor the state of the Userflow resource center (open aka expanded vs. closed) to determine if the minimize button should be hidden
function monitorResourceCenterState() {
    const minimizeUserflowButton = document.getElementById('minimizeUserflowButton');

    function updateState() {
        const resourceCenterElement = document.querySelector('.userflowjs-resource-center-frame');
        if (resourceCenterElement && minimizeUserflowButton) {
            if (resourceCenterElement.classList.contains('userflowjs-resource-center-frame--open')) {
                minimizeUserflowButton.style.opacity = '0';
                minimizeUserflowButton.style.pointerEvents = 'none';
            } else if (resourceCenterElement.classList.contains('userflowjs-resource-center-frame--closed')) {
                minimizeUserflowButton.style.opacity = '1';
                minimizeUserflowButton.style.pointerEvents = 'auto';
            } else {
                console.log('Resource Center state unclear or icon not visible. Hide button is hidden.');
            }
        }
    }

    // Initial state check
    updateState();

    // Polling method to check for state updates
    setInterval(updateState, 500);
}

// Call this after buttons are initialized
monitorAndMinimizeUserflow();
monitorResourceCenterState();
